/**
 * HAR-19 (Feb 2026) — OfflineAwareImage: dành cho public avatar / DiceBear /
 * logo nhỏ (viewer, chat, follow, sponsor logos).
 *
 * Vấn đề trước đây: hàng chục <img> avatar cùng render → khi mạng vừa
 * reconnect, browser bắn "burst" 30-60 request cùng lúc → CF Worker phải
 * gánh, edge cache cold → origin lũ về. LazyThumb có xử lý viewport nhưng
 * chưa kết hợp trạng thái Network Governor.
 *
 * Quy tắc:
 *   • Offline (confirmed) → luôn placeholder, KHÔNG gắn `src` (browser sẽ
 *     KHÔNG thêm request vào queue chờ reconnect).
 *   • Online + viewport miss → placeholder (giống LazyThumb, rootMargin 200px).
 *   • Online + in viewport → gắn `src` thật. Nếu chuyển sang offline lại thì
 *     giữ nguyên `src` đã tải (không thu hồi).
 *   • Lỗi → placeholder, KHÔNG retry.
 *
 * KHÔNG dùng cho hero banner / poster / VOD thumb (đã có LazyThumb + kích
 * thước lớn). Chỉ dùng cho ảnh nhỏ (<=128px) tần suất render cao.
 */
import { useEffect, useRef, useState } from "react";
import * as governor from "../../lib/networkGovernor";

const _DEFAULT_PLACEHOLDER =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'><rect width='100%' height='100%' fill='%23e5e7eb'/></svg>";

export const OfflineAwareImage = ({
  src,
  alt = "",
  className = "",
  width,
  height,
  rootMargin = "200px",
  placeholder,
  fallbackSrc = _DEFAULT_PLACEHOLDER,
  testId,
  ...rest
}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);  // rerender khi governor state đổi

  // Subscribe governor: chuyển online → rerender để cho phép hydrate ảnh.
  useEffect(() => {
    if (!governor.subscribe) return undefined;
    return governor.subscribe(() => setTick((t) => t + 1));
  }, []);

  // IntersectionObserver — lazy vào viewport.
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView, rootMargin]);

  const isOff = !!(governor.isOffline && governor.isOffline());
  // Nếu offline + chưa có ảnh nào từng tải → luôn placeholder.
  const canLoad = inView && !failed && src && !isOff;

  return (
    <img
      ref={ref}
      src={canLoad ? src : fallbackSrc}
      alt={alt}
      className={className}
      loading="lazy"
      decoding="async"
      width={width}
      height={height}
      data-testid={testId}
      data-off={isOff ? "1" : "0"}
      data-tick={tick}
      onError={() => setFailed(true)}
      {...rest}
    />
  );
};

export default OfflineAwareImage;
